<template>
  <v-menu
    v-model="menuOpen"
    @update:model-value="$emit('open', $event)"
    :close-on-content-click="true"
    :data-testid="dataTestid ? `${dataTestid}-menu` : 'standard-action-menu-menu'"
    :location="menuLocation"
    data-cy="standard-action-menu"
  >
    <template #activator="{ props: activatorProps }">
      <StandardButton
        v-show="items.length > 0"
        :aria-label="$bct(label)"
        :bind-props="activatorProps"
        :custom-classes="activatorCustomClasses"
        :data-testid="dataTestid || 'standard-action-menu-button'"
        :disabled-text="disabledText"
        :icon="icon"
        :loading="loading"
        :prevent-double-click="false"
        color="black"
        size="small"
        variant="text"
        suppress-parent-click
      />
    </template>

    <v-list
      v-if="items.length > 0"
      :data-testid="dataTestid ? `${dataTestid}-list` : 'standard-action-menu-list'"
      class="b-1 py-0"
      elevation="0"
      tile
    >
      <div
        v-for="(menuItem, index) in items"
        :key="`standard-action-menu-item-${index}`"
      >
        <v-list-item
          @click="handleMenuItemClick(menuItem)"
          :aria-label="itemAriaLabel(menuItem)"
          :data-testid="menuItem.testId || 'standard-action-menu-list-item'"
          :disabled="menuItem.disabled ? true : null"
          :lines="menuItem.subtitle ? 'two' : undefined"
          :subtitle="itemSubtitle(menuItem)"
          :title="itemTitle(menuItem)"
        >
          <template
            v-if="menuItem.avatar"
            #prepend
          >
            <v-avatar
              :size="avatarSize(menuItem)"
              rounded="0"
            >
              <v-icon
                :icon="menuItem.avatar"
                :size="avatarSize(menuItem)"
              />
            </v-avatar>
          </template>

          <template
            v-if="menuItem.action"
            #append
          >
            <v-icon :icon="menuItem.action" />
          </template>
        </v-list-item>

        <v-divider
          v-if="index + 1 < items.length"
          :key="`${index}-divider`"
          class="bc-black"
        />
      </div>
    </v-list>
  </v-menu>
</template>

<script setup>
import { useRouter } from 'vue-router';

const $bct = inject('translator');

const emit = defineEmits(['action-clicked', 'open']);

const props = defineProps({
  activatorClass: {
    type: String,
    default: '',
  },
  dataTestid: {
    type: String,
    default: null,
  },
  disabledText: {
    type: String,
    default: null,
  },
  icon: {
    type: String,
    default: 'more_vert',
  },
  items: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: 'More actions',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  left: {
    type: Boolean,
    default: false,
  },
  noTranslate: {
    type: Boolean,
    default: false,
  },
  right: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const menuOpen = ref(false);

const activatorCustomClasses = computed(() =>
  ['standard-action-menu__activator', 'c-black', props.activatorClass].filter(Boolean).join(' '),
);

const menuLocation = computed(() => {
  if (props.left) return 'bottom start';
  if (props.right) return 'bottom end';
  return 'bottom end';
});

function avatarSize(menuItem) {
  return menuItem?.subtitle ? 25 : 20;
}

function itemTitle(menuItem) {
  if (props.noTranslate) return menuItem.title;
  return $bct(menuItem.title);
}

function itemSubtitle(menuItem) {
  if (!menuItem.subtitle) return undefined;
  if (props.noTranslate) return menuItem.subtitle;
  return $bct(menuItem.subtitle);
}

function itemAriaLabel(menuItem) {
  if (props.noTranslate) return menuItem.title;
  return $bct(menuItem.title);
}

function handleMenuItemClick(menuItem) {
  menuOpen.value = false;

  if (menuItem.link) {
    void router.push(menuItem.link);
    return;
  }

  if (menuItem.href) {
    window.open(menuItem.href, '_blank', 'noopener,noreferrer');
    return;
  }

  if (menuItem.event) {
    emit('action-clicked', { action: menuItem.event, item: menuItem });
  }
}
</script>

<style scoped>
:deep(.v-list-item-subtitle) {
  line-height: 1.5rem;
}
</style>
